import { useNavigation, useRoute } from "@react-navigation/native";
import { Column, Grid, Row } from 'components/atoms/BootstrapGrid';
import HStack from "components/atoms/stack/HStack";
import VStack from 'components/atoms/stack/VStack';
import ThemedBlock from "components/atoms/ThemedBlock";
import ThemedLayout, { createRefreshControl } from "components/atoms/ThemedLayout";
import ThemedPressable from 'components/atoms/ThemedPressable';
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import FetchError from "components/molecules/FetchError";
import FooterBar from 'components/organisms/FooterBar';
import FormRow from 'components/organisms/form/FormRow';
import MutationEmployeeTransport from "graphql/mutations/MutationEmployeeTransport";
import _ from "lodash";
import { PlaceholderMessage } from 'placeholders';
import React, { useEffect, useRef, useState } from "react";
import { StyleSheet, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import HomeButtonView from 'scenes/home/presence/_components/HomeButtonView';
import PresenceMessageView from 'scenes/home/presence/_components/MessageView';
import TimeslotKindView from 'scenes/home/presence/_components/TimeslotKindView';
import { dataConstants } from "services/redux/data/constants";
import { textProps, textStyles } from "styles/text";
import { ThemedValues } from "styles/themed";
import Button from "theme/Button";
import Input from "theme/Input";
import Text from "theme/Text";
import { colorList } from "utils/colors";
import { MODE_SCAN_OUT } from 'utils/constants/qrcode-constants';
import { settingsConstants } from 'utils/constants/settings-constants';
import { transportList, transportListLicensePlate } from "utils/constants/transport-constants";
import FetchEmployeeTransport from "utils/fetch/FetchEmployeeTransport";
import HelperSetting from 'utils/helper-setting';
import HelperString from 'utils/helper-string';
import i18n from "utils/i18n";
import ObjectEmployeeTransport from "utils/objects/employee-transport";
import ObjectTransport from "utils/objects/transport";
import GraphqlBuilder from "utils/request/graphql/builder";
import { addDataByGuid } from "services/redux/data/actions";

const HomePresenceTransportScene = () => {
	
	const navigation = useNavigation();
	const route = useRoute();
	const dispatch = useDispatch()
	
	const layoutRef = useRef()
	
	const _employeeTransports = useSelector( state => {
		return state.data[dataConstants.EMPLOYEE_TRANSPORTS];
	} )
	const _timeslotGroups = useSelector( state => {
		return state.data[dataConstants.TIMESLOT_GROUPS];
	} );
	const _timeslotKinds = useSelector( state => {
		return state.data[dataConstants.TIMESLOT_KINDS];
	} );
	
	const [ loading, setLoading ] = useState( true )
	const [ error, setError ] = useState( false )
	const [ refreshing, setRefreshing ] = useState( false )
	const [ mutating, setMutating ] = useState( false )
	const [ forceChange, setForceChange ] = useState( false )
	const [ transportKind, setTransportKind ] = useState( null )
	const [ licensePlate, setLicensePlate ] = useState( null )
	
	const transportSelectEnabled = HelperSetting.isYes( settingsConstants.BOOKING_ENABLE_TRANSPORT_SELECT );
	
	const buildingGuid = _.get( route, ['params', 'buildingGuid'], null )
	const timeslotGroupGuid = _.get( route, ['params', 'timeslotGroupGuid'], null )
	const timeslotKindGuid = _.get( route, ['params', 'timeslotKindGuid'], null )
	const message = _.get( route, ['params', 'message'], null )
	const navigatedFromScan = _.get( route, ['params', 'navigatedFromScan'], false )
	const navigatedFromScanForced = _.get( route, ['params', 'navigatedFromScanForced'], false )
	const scanMode = _.get( route, ['params', 'scanMode'], null )
	
	const timeslotGroup = _.get( _timeslotGroups, timeslotGroupGuid, null )
	const timeslotKind = _.find( _timeslotKinds, { guid: timeslotKindGuid } )
	
	// Get the transports for this building and for today
	const firstTransport = _.first( _.filter( ObjectEmployeeTransport.filterAndOrder( _employeeTransports ), o => {
		return buildingGuid === null || o?.building?.guid === buildingGuid
	}) );
	
	// Check if we can navigate back
	const hideTransportSelect = !transportSelectEnabled || scanMode === MODE_SCAN_OUT || !_.isEmpty( timeslotGroupGuid ) || !_.isEmpty( timeslotKindGuid );
	
	const canNavigateBack = ( hideTransportSelect || !_.isEmpty( firstTransport ) ) && !navigatedFromScanForced;
	
	const fetchEmployeeTransports = () => {
		FetchEmployeeTransport.fetch({
			callbackStart: setError.bind( this, false ),
			dispatchDataToStore: true,
			/* TODO optimize */
			callbackError: () => {
				setLoading( false )
				setRefreshing( false )
				setError( true )
			},
			callbackSuccess: () => {
				setLoading( false )
				setRefreshing( false )
			}
		});
	}
	
	const saveEmployeeTransport = () => {
		new GraphqlBuilder()
			.addQuery( 'mutation_employee_transport', MutationEmployeeTransport, {
				building_guid: buildingGuid,
				transport_kind: transportKind,
				license_plate: transportListLicensePlate.includes( transportKind ?? firstTransport?.transport_kind ) ? licensePlate : 'empty'
			})
			.setResultPath( ['mutation_employee_transport', 'employee_transport'], {} )
			.setOnStart( () => {
				setMutating( true )
			})
			.setOnSuccess( ( result ) => {
				dispatch( addDataByGuid( dataConstants.EMPLOYEE_TRANSPORTS, [ result ] ) )
				
				setMutating( false )
				setForceChange( false )
				
				onPressHome();
			})
			.setOnError( () => {
				setMutating( false )
			})
			.execute()
	}
	
	const onPressHome = () => {
       navigation.popToTop()
	}
	
	useEffect( () => {
		if( !hideTransportSelect ) {
			navigation.setOptions( {
				gestureEnabled: !_.isEmpty( firstTransport )
			} )
		}
	}, [ _employeeTransports, canNavigateBack ] )
	
	useEffect( () => {
		fetchEmployeeTransports()
	}, [])
	
	const isEditing = !hideTransportSelect && ( forceChange || _.isEmpty( firstTransport ) );
	
	let tmpTransportKind = transportKind;
	if( !isEditing && !_.isEmpty( firstTransport ) ) {
		tmpTransportKind = firstTransport.transport_kind;
	}
	
	let footerBar = null;
	
	let content = <PlaceholderMessage />;
	if( !loading ) {
		
		// On error show this
		if( error ) {
			content = (
				<FetchError
					action={ fetchEmployeeTransports }
				/>
			)
		} else {
			content = [];
			
			//
			if( message !== null ) {
				content.push(
					<PresenceMessageView
						key={ HelperString.hash('transport', 'message' ) }
						message={ message }
						timeslotGroup={ timeslotGroup }
						timeslotKind={ timeslotKind }
					/>
				);
			}
			
			switch( true ) {
				case !_.isEmpty( timeslotGroup ) || !_.isEmpty( timeslotKind ):
					footerBar = (
						<HomeButtonView
							onPress={ onPressHome }
						/>
					)
					
					content.push(
						<TimeslotKindView
							timeslotKind={ timeslotKind }
							onPressHome={ onPressHome }
						/>
					)
					break;
					
				case !isEditing:
					content.push(
						<ThemedBlock
							key={ HelperString.hash('transport', 'not-editing' ) }
							shadow
							mediumPadding>
							
							{ tmpTransportKind !== null && (
								<VStack>
									<Text
										style={ textStyles.textCenter }>
										
										{ i18n.t('page.transport.text_selected', {
											kind: i18n.t(`basic.transport.kind.${ tmpTransportKind }_sentence` )
										})}
									</Text>
									
									{ firstTransport?.license_plate?.license_plate && (
										<View
											style={styles.viewAlignSelfCenterWidthAuto}>
											
											<Text
												style={ textStyles.textCenter }>
												
												{ _.upperCase( firstTransport?.license_plate?.license_plate ) }
											</Text>
										</View>
									)}
								</VStack>
							)}
							
							{ !hideTransportSelect && (
								<Button
									style={ styles.buttonMarginTop16 }
									onPress={ setForceChange.bind( this, true ) }
									appearance={ "ghost" }>
									
									{ i18n.t('page.transport.button_change') }
								</Button>
							)}
						</ThemedBlock>
					)
					
					if( navigatedFromScan || navigatedFromScanForced ) {
						footerBar = (
							<HomeButtonView
								onPress={ onPressHome }
							/>
						)
					}
					break;
					
				default:
					content.push(
						<ThemedBlock
							key={ HelperString.hash('transport', 'editing' ) }
							shadow
							mediumPadding>
							
							<VStack
								space={ 8 }>
								
								<VStack>
									<Text>{ i18n.t('page.transport.intro') }</Text>
										
									{ _.map( _.chunk( transportList, 2 ), (chunk, key) => {
										return (
											<HStack
												key={ HelperString.hash( 'transport-list', key ) }>

												{_.map( chunk, item => {
													const selected = ( transportKind !== null ? transportKind === item : firstTransport?.transport_kind === item );
													const color = selected ? colorList.PRIMARY.primary : colorList.TEXT.primary;

													return (
														<ThemedPressable
															onPress={ setTransportKind.bind( true, item ) }>

															<ThemedBlock
																shadow
																mediumPadding
																style={ [
																	styles.selectItem,
																	selected ? styles.selectItemSelected : {},
																] }>

																{ ObjectTransport.getIcon( item, {
																	size: 24,
																	color: color
																}) }

																<Text
																	{ ...textProps.fit }
																	style={ [
																		styles.selectItemText,
																		{
																			color: color
																		}
																	] }>

																	{ i18n.t(`basic.transport.kind.${item}`) }
																</Text>
															</ThemedBlock>
														</ThemedPressable>
													)
												})}
											</HStack>
										);
									})}
								</VStack>
								
								{/* Show license plate */}
								{ transportListLicensePlate.includes( transportKind ?? firstTransport?.transport_kind ) && (
									<VStack>
										<FormRow
											title={ i18n.t('form.fields.license_plate.title') }>
											
											<Input
												defaultValue={ firstTransport?.license_plate?.license_plate ?? '' }
												blurOnSubmit={ false }
												autoCorrect={ false }
												onChangeText={ value => {
													return setLicensePlate(value);
												} }
											/>
										</FormRow>
									</VStack>
								)}
								
								<VStack>
									<Button
										disabled={ transportKind === null }
										isLoading={ mutating }
										onPress={ saveEmployeeTransport }>
										
										{ i18n.t('page.transport.save') }
									</Button>
									
									{ !_.isEmpty( transportKind ) && (
										<Button
											onPress={ setForceChange.bind( this, false ) }
											appearance={ "ghost" }>
											
											{ i18n.t('page.transport.button_cancel') }
										</Button>
									)}
								</VStack>
							</VStack>
						</ThemedBlock>
					)
			}
		}
	}
	
	return (
		<ThemedSafeAreaComponentTop
			top={ false }>
			
			<ThemedLayout
				ref={ layoutRef }
				topNavigation={ true }
				footerBar={ footerBar !== null }
				showBackButton={ canNavigateBack }
				title={ hideTransportSelect ? '' : i18n.t( 'page.transport.title') }
				scrollViewProps={{
					...createRefreshControl({
						isRefreshing: refreshing,
						onRefresh: () => {
							setRefreshing( true )
							fetchEmployeeTransports()
						}
					})
				}}>
				
				{ content }
			</ThemedLayout>
			
			{ footerBar && (
				<FooterBar
					buttons={ footerBar }
					onHeightCallback={ layoutRef.current?.setFooterHeight }
				/>
			) }
		</ThemedSafeAreaComponentTop>
	);
}

export default HomePresenceTransportScene

export const styles = StyleSheet.create({
	viewAlignSelfCenterWidthAuto: {
		alignSelf: 'center',
		width: 'auto',
		borderRadius: 4,
		backgroundColor: colorList.GREY.light,
	},
	buttonMarginTop16: {
		marginTop: 16,
	},
	messageContainer: {
		width: '100%',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		marginTop: ThemedValues.marginHorizontal
	},
	messageIcon: {
		justifyContent: 'center',
		alignItems: 'flex-end',
	},
	
	selectContainer: {
		width: '100%'
	},
	selectItem: {
		flex: 1,
		minHeight: 50,
		alignItems: 'center',
		marginBottom: 0,
	},
	selectItemSelected: {
		shadowColor: colorList.PRIMARY.primary,
	},
	selectItemText: {
		textAlign: 'center'
	}
})
